import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { EntitySelector } from '@bingads-webui-react/entity-selector';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Checkbox } from '@fluentui/react-components';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';

const findItemIndex = (list, item) => {
  for (let i = 0; i < list.length; i += 1) {
    if (item.Id === list[i].Id) {
      return i;
    }
  }
  return -1;
};

const getStyle = () => ({
  header: {
    padding: '8px',
  },
  checkboxColumn: {
  },
  accountColumn: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowItem: {
    display: 'flex',
    alignItems: 'baseline',
    maxWidth: 254,
  },
});

export const MultiAccountSelector = ({
  i18n,
  onSelect,
  onDeselect,
  fetch,
  pageSize,
}) => {
  // items, // items in current page
  // totalCount, // total count of items
  // selectedItems, // selected items in all pages
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const classes = useDefaultStyles(getStyle);

  const renderHeader = () => (
    <div className={`account-selector-header ${classes.header}`}>
      <div className={classes.checkboxColumn} />
      <div className={classes.accountColumn}>
        <b>
          {i18n.getString(_TL_('Account'))}
        </b>
      </div>
    </div>
  );

  const renderItem = (item, index, forwardedRef) => {
    const isSelected = findItemIndex(selectedItems, item) > -1;

    return (
      <div className={`account-selector-rowitem ${classes.rowItem}`}>
        <div className={classes.checkboxColumn} ref={forwardedRef}>
          <Checkbox
            checked={isSelected}
            // onChange={() => this.props.onSelect(item)}
            aria-label={item.Name}
          />
        </div>
        <div className={classes.accountColumn}>
          {item.Name}
        </div>
      </div>
    );
  };

  const renderEmptyList = () => (
    <div className="empty-content" role="option" aria-selected="false">
      {i18n.getString(_TL_('No results to display.'))}
    </div>
  );

  const renderActionBar = () => (
    <div
      className="select-all-content"
      onClick={onSelectAllClick}
      onKeyDown={submitEvent(() => onSelectAllClick())}
      role="menuitem"
      tabIndex={0}
    >
      <span>
        {i18n.getString(_TL_('Select all on this page'))}
      </span>
    </div>
  );

  const onSelectAllClick = () => {
    const updatedItems = _.filter(items, item => findItemIndex(selectedItems, item) === -1);
    const newSelectedItems = [...selectedItems, ...updatedItems];
    setSelectedItems(newSelectedItems);
    onSelect(items, newSelectedItems);
  };

  const onClickItem = (selectedItem) => {
    const isSelected = findItemIndex(selectedItems, selectedItem) > -1;
    const newSelectedItems = isSelected ? _.filter(selectedItems, item => item.Id !== selectedItem.Id) : [...selectedItems, selectedItem];
    setSelectedItems(newSelectedItems);
    if (isSelected) {
      onDeselect(selectedItem, newSelectedItems);
    } else {
      onSelect(selectedItem, newSelectedItems);
    }
  };

  const fetchItems = ({ query = '', pageNumber = 0 } = {}) => {
    fetch({ query, pageNumber, pageSize })
      .then((data) => {
        setItems(data.items);
        setTotalCount(data.totalCount);
      });
  };

  useEffect(() => {
    fetchItems({ query: '', pageNumber: 0 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EntitySelector
      onFetch={fetchItems}
      renderHeader={renderHeader}
      renderItem={renderItem}
      renderEmptyList={renderEmptyList}
      renderActionBar={renderActionBar}
      onSelect={onClickItem}
      items={items}
      totalCount={totalCount}
      i18n={i18n}
      pageSize={pageSize}
      enableArrowKeysSupport
      ariaLabel={i18n.getString(_TL_('Account list'))}
      searchBoxAriaLabel={i18n.getString(_TL_('Search by account name'))}
      enableOverflowTooltip
    />
  );
};

MultiAccountSelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func.isRequired,
  fetch: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
};

const getAccountSelectorContainerStyle = () => ({
  container: {
    display: 'flex',
  },
});

export function withMultiAccountListSelector(WrappedComponent) {
  const AccountListSelector = (props) => {
    const { multiAccountSelectorOptions } = props;
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const onUpdate = useCallback((item, selectedItems) => {
      setSelectedAccounts(selectedItems);
    }, []);
    const classes = useDefaultStyles(getAccountSelectorContainerStyle);

    return (
      <div className={`account-selector-container ${classes.container}`}>
        <MultiAccountSelector
          {...props}
          {...multiAccountSelectorOptions}
          onSelect={onUpdate}
          onDeselect={onUpdate}
        />
        <WrappedComponent {...props} accounts={selectedAccounts} enableAccountScope />
      </div>
    );
  };

  AccountListSelector.propTypes = {
    multiAccountSelectorOptions: PropTypes.shape({
    }).isRequired,
  };
  return AccountListSelector;
}
